import type { SessionUser, SubmissionStatus } from '@motoveo/shared';
import { SUBMISSION_STATUSES } from '@motoveo/shared';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import { Layout } from '../components/Layout';
import { formatCents, formatDate, formatMiles } from '../lib/format';

export function SubmissionsListPage({ user }: { user: SessionUser }) {
  const navigate = useNavigate();
  const [status, setStatus] = useState<SubmissionStatus | ''>('');

  const list = useQuery({
    queryKey: ['submissions', status],
    queryFn: () => api.submissions.list({ status: status || undefined, limit: 200 }),
  });

  return (
    <Layout user={user}>
      <div className="row-between">
        <h2>Submissions</h2>
        <button onClick={() => navigate('/intake')}>+ New intake</button>
      </div>

      <div className="toolbar">
        <label className="inline">
          Status
          <select value={status} onChange={(e) => setStatus(e.target.value as SubmissionStatus | '')}>
            <option value="">all</option>
            {SUBMISSION_STATUSES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </label>
        {list.data ? <span className="muted">{list.data.total} total</span> : null}
      </div>

      {list.isLoading ? (
        <p className="muted">Loading…</p>
      ) : list.isError ? (
        <p className="error">{(list.error as Error).message}</p>
      ) : list.data && list.data.items.length > 0 ? (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Ref</th>
                <th>Status</th>
                <th>Source</th>
                <th>Vehicle</th>
                <th>Location</th>
                <th>Mileage</th>
                <th>Asking</th>
                <th>Consent</th>
                <th>Media</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {list.data.items.map((s) => (
                <tr key={s.id} className="clickable" onClick={() => navigate(`/submissions/${s.id}`)}>
                  <td>
                    <Link to={`/submissions/${s.id}`} onClick={(e) => e.stopPropagation()}>
                      {s.public_ref}
                    </Link>
                  </td>
                  <td>
                    <span className="pill">{s.status}</span>
                  </td>
                  <td className="muted">{s.source}</td>
                  <td>
                    {s.vehicle_model ? `${s.vehicle_model_year ?? ''} ${s.vehicle_model}`.trim() : s.raw_vin ?? '—'}
                    {s.vin_valid === false ? <span className="warn"> · VIN?</span> : null}
                  </td>
                  <td className="muted">{s.seller_region_label ?? s.seller_state ?? '—'}</td>
                  <td>{formatMiles(s.stated_mileage)}</td>
                  <td>{formatCents(s.asking_price_cents)}</td>
                  <td>{s.has_consent ? <span className="ok-dot">●</span> : <span className="muted">—</span>}</td>
                  <td className="muted">{s.media_count}</td>
                  <td className="muted">{formatDate(s.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="muted">No submissions yet. Start one with “New intake”.</p>
      )}
    </Layout>
  );
}
